import { useTranslation } from "@next-platform/client-common-core";
import RulebookPickerTab from "@next-platform/client-common-core/rulebook/picker/RulebookPickerTab";
import useRulebookPickerState from "@next-platform/client-common-core/rulebook/picker/useRulebookPickerState";
import { IconVariants, Input, Modal, ModalSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { RulebookSummary } from "@next-platform/packages-ts-types";
import React, { memo, useCallback, useMemo } from "react";

import Trans from "@/components/Trans/Trans";

import { RulebookSortControl, RulebookTabControl } from "./ModalRulebookControls";
import ModalRulebookList from "./ModalRulebookList";
import { useRulebookDiscoverConfig } from "./rulebookDiscover";
import RulebookDiscoverTab from "./RulebookDiscoverTab";
import RulebookOwnerCopy, { type RulebookTarget } from "./RulebookOwnerCopy";
import useModalRulebookActions from "./useModalRulebookActions";

type ModalRulebookProps = {
  /** What the picked Rulebook will be attached to; picks the owner-specific description. */
  ownerType: RulebookTarget;
  isOpen: boolean;
  onClose: () => void;
  rulebooks: RulebookSummary[] | null;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  /** The Rulebook currently picked in the form, preselected (Replace stays disabled until a different one is chosen). */
  selectedRulebookId: string | null;
  /** The picked Rulebook's summary comes along, so a public pick (not among the user's own) can be shown. */
  onRulebookSelected: (rulebookId: string, rulebook?: RulebookSummary) => void;
  dataTestId?: string;
};

type ToolbarProps = {
  dataTestId: string;
  search: string;
  onSearch: (value?: string) => void;
  count: number;
  renderControls: () => React.ReactNode;
};

/** Search box, result count and the shared controls (tab + sort), as in the Lorebook picker. */
const ModalRulebookToolbar = memo(({ dataTestId, search, onSearch, count, renderControls }: ToolbarProps) => {
  const { t } = useTranslation();
  const countValues = useMemo(() => ({ count }), [count]);
  return (
    <>
      <Input
        placeholder={t("rulebookEditor:attach.modal.search", "Search Rulebooks")}
        iconStart={IconVariants.SEARCH}
        value={search}
        onChange={onSearch}
        data-testid={`${dataTestId}-Search`}
      />
      <div className="flex items-center justify-between">
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
          <Trans i18nKey="common:results.found" values={countValues} />
        </Text>
        <div className="flex items-center gap-2">{renderControls()}</div>
      </div>
    </>
  );
});
ModalRulebookToolbar.displayName = "ModalRulebookToolbar";

/**
 * Picker for the Rulebook attached to a character, persona or chat — the Rulebook counterpart of `ModalLorebook`.
 * My Rulebooks lists the user's own; Discover (behind `rulebook.publicDiscovery`, once the public index exists) searches
 * approved public Rulebooks.
 */
const ModalRulebook = ({
  ownerType,
  isOpen,
  onClose,
  rulebooks,
  isLoading,
  hasError,
  onRetry,
  selectedRulebookId,
  onRulebookSelected,
  dataTestId = "ModalRulebook",
}: ModalRulebookProps) => {
  const { t } = useTranslation();
  const discover = useRulebookDiscoverConfig();
  const picker = useRulebookPickerState({ isOpen, rulebooks, selectedRulebookId });
  const { stateTab, stateSort, setStateSort, statePick, handleTab, isUnchanged } = picker;
  const isDiscoverTab = discover.isAvailable && stateTab === RulebookPickerTab.Discover;
  const renderControls = useCallback(
    () => (
      <>
        {discover.isAvailable ? <RulebookTabControl tab={stateTab} onTab={handleTab} dataTestId={dataTestId} /> : null}
        <RulebookSortControl sort={stateSort} onSort={setStateSort} dataTestId={dataTestId} />
      </>
    ),
    [discover.isAvailable, stateTab, handleTab, stateSort, setStateSort, dataTestId],
  );

  // With Discover there's always something to pick from, even for a creator with no Rulebooks of their own.
  const hasChoices = Boolean(rulebooks?.length) || discover.isAvailable;
  const actions = useModalRulebookActions({
    tab: stateTab,
    pick: statePick,
    isUnchanged,
    rulebooks,
    isLoading,
    hasError,
    isDiscoverTab,
    hasChoices,
    selectedRulebookId,
    onRulebookSelected,
    onClose,
  });

  return (
    <Modal
      data-testid={`${dataTestId}-Modal`}
      title={t("rulebookEditor:attach.modal.title", "Attach Rulebook")}
      open={isOpen}
      onOpenChange={onClose}
      actions={actions}
      size={ModalSize.MEDIUM}
      description={
        <div className="flex flex-col gap-4" data-testid={dataTestId}>
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
            <RulebookOwnerCopy ownerType={ownerType} place="picker" />
          </Text>
          {hasChoices && !isDiscoverTab ? (
            <ModalRulebookToolbar
              dataTestId={dataTestId}
              search={picker.stateSearch}
              onSearch={picker.handleSearch}
              count={picker.visibleRulebooks.length}
              renderControls={renderControls}
            />
          ) : null}
        </div>
      }
    >
      {isDiscoverTab && discover.apiKey && discover.collectionName ? (
        <RulebookDiscoverTab
          apiKey={discover.apiKey}
          collectionName={discover.collectionName}
          sort={stateSort}
          pickedId={statePick.id}
          picked={statePick.rulebook}
          onPick={picker.pickDiscovered}
          renderControls={renderControls}
        />
      ) : (
        <div className="flex min-h-[320px] flex-col gap-md" data-testid={`${dataTestId}-List`}>
          <ModalRulebookList
            rulebooks={rulebooks}
            visibleRulebooks={picker.visibleRulebooks}
            isLoading={isLoading}
            hasError={hasError}
            onRetry={onRetry}
            pickedId={statePick.id}
            onPick={picker.pickMine}
            dataTestId={dataTestId}
          />
        </div>
      )}
    </Modal>
  );
};

export default memo(ModalRulebook);
